import { Observable, Subject } from 'rxjs';
import { debounceTime, takeUntil } from 'rxjs/operators';

import { SelectionModel } from '@angular/cdk/collections';
import { Component, Inject, OnDestroy, OnInit, ViewChild } from '@angular/core';
import { UntypedFormControl } from '@angular/forms';
import { MatDialogRef, MAT_DIALOG_DATA } from '@angular/material/dialog';

import { ISnListQueryParameters, ISnListResponse, SortParameters } from '@lib/api';
import { SnUser } from '@lib/api/entities';
import { SnUsersAPI } from '@lib/api/services';
import { SnDataListComponent } from '@lib/ui/data-list';

@Component({
  selector: 'sn-user-search-dialog',
  templateUrl: './user-search-dialog.component.html',
  styleUrls: ['./user-search-dialog.component.scss'],
})
export class UserSearchDialogComponent implements OnInit, OnDestroy {
  @ViewChild('list', { static: false })
  public readonly list: SnDataListComponent<SnUser>;
  public readonly loadData = this._loadData.bind(this);
  public selection = new SelectionModel<SnUser>(true, []);

  public readonly searchControl: UntypedFormControl = new UntypedFormControl();
  public readonly columns: string[] = ['select', 'firstName', 'lastName', 'email', 'lastLogin'];
  public _querySearch: string;

  private readonly _destroy$ = new Subject();

  constructor(
    @Inject(MAT_DIALOG_DATA) public data: any,
    private readonly _dialogRef: MatDialogRef<UserSearchDialogComponent>,
    private readonly _usersAPI: SnUsersAPI,
  ) {}

  public ngOnInit(): void {
    this.searchControl.valueChanges
      .pipe(debounceTime(300), takeUntil(this._destroy$))
      .subscribe((v) => {
        this._querySearch = v;
        this._reloadData();
      });
  }

  public ngOnDestroy(): void {
    this._destroy$.next();
    this._destroy$.complete();
  }

  public submitForm(): void {
    this._querySearch = this.searchControl.value;
    this._reloadData();
  }

  public clear(): void {
    this.searchControl.reset(null);
    this.submitForm();
  }

  public onSelected(camera: any): void {
    this._dialogRef.close(camera);
  }

  public cancel(): void {
    this._dialogRef.close();
  }

  public isAllSelected(): boolean {
    const numSelected = this.selection.selected.length;
    const numRows = this.list.dataSource.data.length;
    return numSelected === numRows;
  }

  public masterToggle(): void {
    this.isAllSelected()
      ? this.selection.clear()
      : this.list.dataSource.data.forEach((row) => this.selection.select(row));
  }

  public call(): void {
    const cameras = this.selection.selected.map((camera) => camera);
    this.onSelected(cameras);
  }

  private _reloadData(): void {
    this.list.reference.reload();
  }

  private _loadData(params?: ISnListQueryParameters): Observable<ISnListResponse<SnUser>> {
    params.search = this._querySearch;
    return this._usersAPI
      .getUserList(this._prepareQueryParams(params))
      .pipe(takeUntil(this._destroy$));
  }

  private _prepareQueryParams<T extends ISnListQueryParameters>(params: T): T {
    return {
      ordering: {
        active: 'name',
        direction: 'asc',
      },
      status__in: 'Up, Unmanaged',
      ...params,
    };
  }
}
